import { z } from "zod";
import type { PaymentTerms } from "./invoice.js";
import { PayScheduleRow } from "./payment.js";

/**
 * 分割払い（#267 3b、docs/97 決めごと10〜12・§11 ★6。門はモジュール sales・両製品）。
 *   分割請求（042）＝請求を分ける（回ごとに請求書）。分割払い＝支払を分ける（請求書は1枚・税は1回、その請求書に支払予定）。
 *   回数と間隔は{customer}の支払条件（既定 なし）→ 発行の確認で1枚だけ上書き。発行の瞬間に DB が支払予定を作る（payment_schedules）。
 *   期日と額は DB の fn_payment_schedule_plan が正。ここの scheduleDatesOf・scheduleAmountsOf は支払条件の窓の「例」の表示だけに使い、
 *   同じ答えになることを PAY_SCHEDULE_CASES で Vitest（contract）と API のテスト（DB）の両方で確かめる
 */

/** 回数 2〜12・間隔 1〜12 か月 */
export const InstallmentPlan = z.object({
  count: z.number().int().min(2).max(12),
  intervalMonths: z.number().int().min(1).max(12),
});
export type InstallmentPlan = z.infer<typeof InstallmentPlan>;

/** 発行の確認の選択：client＝{customer}の設定／none＝分割しない／回数と間隔＝この1枚だけ */
export const InstallmentChoice = z.union([z.literal("client"), z.literal("none"), InstallmentPlan]);
export type InstallmentChoice = z.infer<typeof InstallmentChoice>;

/** 画面の選択肢（回数・間隔） */
export const INSTALLMENT_COUNTS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;
export const INSTALLMENT_INTERVALS = [1, 2, 3, 6] as const;

/** 「毎月」「2か月ごと」 */
export function intervalLabel(months: number): string {
  return months <= 1 ? "毎月" : `${months}か月ごと`;
}
/** 「3回 毎月」 */
export function installmentPlanLabel(p: InstallmentPlan): string {
  return `${p.count}回 ${intervalLabel(p.intervalMonths)}`;
}

/** 請求書の分割払い（Invoice.paySchedule）：{customer}の既定と、この1枚の上書き（下書きで選んだもの） */
export const InvoicePaySchedule = z.object({
  /** {customer}の支払条件の分割払い（null＝なし） */
  client: InstallmentPlan.nullable(),
  /** この1枚の上書き（null＝{customer}に従う／"none"＝分割しない） */
  own: z.union([z.literal("none"), InstallmentPlan]).nullable(),
  /**
   * 回（発行した請求書は固めた予定 payment_schedules、下書きは今の選択での試算＝fn_payment_schedule_plan）。2回以上のときだけ、1回なら null。
   * 紙の「お支払い予定」の表とお支払期限の「（第1回）」に使う
   */
  plan: z
    .object({
      count: z.number().int(),
      intervalMonths: z.number().int(),
      rows: z.array(z.object({ seq: z.number().int(), dueOn: z.string(), amount: z.number().int() })),
    })
    .nullable()
    .default(null),
});
export type InvoicePaySchedule = z.infer<typeof InvoicePaySchedule>;

/** POST /invoices/:id/schedule-preview の本文と答え（発行の確認の表。期日と額は DB の fn_payment_schedule_plan） */
export const SchedulePreviewRequest = z.object({ installments: InstallmentChoice });
export type SchedulePreviewRequest = z.infer<typeof SchedulePreviewRequest>;
export const SchedulePreview = z.object({
  /** 決まった回数（1＝分割しない） */
  count: z.number().int(),
  intervalMonths: z.number().int(),
  rows: z.array(z.object({ seq: z.number().int(), dueOn: z.string(), amount: z.number().int() })),
});
export type SchedulePreview = z.infer<typeof SchedulePreview>;

/** {customer}の「入金予定」（GET /partners/:id/receivables）：月ごとの合計と内訳 */
export const ReceivableRow = PayScheduleRow.extend({
  documentId: z.string().uuid(),
  documentNo: z.string().nullable(),
  subject: z.string(),
});
export type ReceivableRow = z.infer<typeof ReceivableRow>;
export const PartnerReceivables = z.object({
  months: z.array(
    z.object({
      /** YYYY-MM（期日の無い回は ""） */
      month: z.string(),
      amount: z.number().int(),
      balance: z.number().int(),
      rows: z.array(ReceivableRow),
    }),
  ),
  balance: z.number().int(),
  overdueBalance: z.number().int(),
});
export type PartnerReceivables = z.infer<typeof PartnerReceivables>;

/* ---------------------------------------------------------------- 期日と額（DB と同じ決まり） */

const ymd = (y: number, m: number, d: number) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const lastDay = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const parts = (s: string) => ({ y: Number(s.slice(0, 4)), m: Number(s.slice(5, 7)), d: Number(s.slice(8, 10)) });
/** DB の fn_month_day：その月の day 日（99＝末日・日がない月は末日） */
function monthDay(y: number, m: number, day: number): string {
  const t = y * 12 + (m - 1);
  const yy = Math.floor(t / 12);
  const mm = (t % 12) + 1;
  const last = lastDay(yy, mm);
  return ymd(yy, mm, day >= 99 || day > last ? last : day);
}

/** お支払期限（DB の fn_payment_due_date の2引数の版と同じ）：締め日 → 支払月 → 支払日 */
export function paymentDueOf(t: PaymentTerms, date: string): string {
  const p = parts(date);
  let close = monthDay(p.y, p.m, t.closingDay);
  if (date > close) close = monthDay(p.y, p.m + 1, t.closingDay);
  const c = parts(close);
  return monthDay(c.y, c.m + t.paymentMonthOffset, t.paymentDay);
}

/** 回の期日（DB の fn_payment_schedule_plan と同じ）：初回＝お支払期限、以後は間隔ごとの同じ日（月末なら月末・日がない月は月末） */
export function scheduleDatesOf(firstDue: string, count: number, intervalMonths: number): string[] {
  const p = parts(firstDue);
  const day = p.d === lastDay(p.y, p.m) ? 99 : p.d;
  const n = Math.max(1, count);
  return Array.from({ length: n }, (_, i) => monthDay(p.y, p.m + i * Math.max(1, intervalMonths), day));
}

/** 回の額（DB の fn_payment_schedule_plan と同じ）：合計÷回数の切り捨て、端数は最終回。回数 1 以下・合計が回数より小さいときは1回 */
export function scheduleAmountsOf(total: number, count: number): number[] {
  const n = count < 2 || total < count ? 1 : count;
  if (n === 1) return [total];
  const each = Math.floor(total / n);
  return Array.from({ length: n }, (_, i) => (i < n - 1 ? each : total - each * (n - 1)));
}

/** DB と TS で同じ答えになるかを確かめる見本（contract の Vitest と API のテストの両方で流す） */
export const PAY_SCHEDULE_CASES: { total: number; firstDue: string; count: number; interval: number; dates: string[]; amounts: number[] }[] = [
  { total: 1427030, firstDue: "2026-11-30", count: 3, interval: 1, dates: ["2026-11-30", "2026-12-31", "2027-01-31"], amounts: [475676, 475676, 475678] },
  { total: 100000, firstDue: "2026-11-20", count: 3, interval: 1, dates: ["2026-11-20", "2026-12-20", "2027-01-20"], amounts: [33333, 33333, 33334] },
  { total: 90000, firstDue: "2026-11-30", count: 3, interval: 2, dates: ["2026-11-30", "2027-01-31", "2027-03-31"], amounts: [30000, 30000, 30000] },
  { total: 30000, firstDue: "2027-01-30", count: 3, interval: 1, dates: ["2027-01-30", "2027-02-28", "2027-03-30"], amounts: [10000, 10000, 10000] },
  { total: 50000, firstDue: "2027-01-31", count: 2, interval: 1, dates: ["2027-01-31", "2027-02-28"], amounts: [25000, 25000] },
  { total: 120000, firstDue: "2026-12-10", count: 12, interval: 1, dates: ["2026-12-10", "2027-01-10", "2027-02-10", "2027-03-10", "2027-04-10", "2027-05-10", "2027-06-10", "2027-07-10", "2027-08-10", "2027-09-10", "2027-10-10", "2027-11-10"], amounts: Array(12).fill(10000) as number[] },
  { total: 100001, firstDue: "2027-02-28", count: 2, interval: 6, dates: ["2027-02-28", "2027-08-31"], amounts: [50000, 50001] },
  { total: 50000, firstDue: "2026-11-30", count: 1, interval: 1, dates: ["2026-11-30"], amounts: [50000] },
  { total: 2, firstDue: "2026-11-30", count: 3, interval: 1, dates: ["2026-11-30"], amounts: [2] },
];

/**
 * 画面の文（{customer} は画面が t()、API が fillLabels で埋める）。見出し・選択肢は名詞だけ、ボタンに「を」「の」を入れない（docs/10 §5）
 */
export const PAY_SCHEDULE_MSG = {
  /** 支払条件の窓のスイッチ・発行の確認の見出し */
  title: "分割払い",
  help: "1枚の請求書の代金を、回に分けてお支払いいただきます（消費税は請求書で1回）。初回はお支払期限、以後は間隔ごとの同じ日です。金額は合計を回数で割り、端数は最終回に入れます。{customer}ごとの設定は、発行の確認で1枚だけ変えられます。",
  count: "回数",
  interval: "間隔",
  countLabel: (n: number) => `${n}回`,
  /** 支払条件の窓の例（10月分の例 11/30・12/31・1/31） */
  example: (label: string) => `${label}の例`,
  /** 発行の確認の選択肢 */
  choiceClient: (plan: string) => `{customer}の設定（${plan}）`,
  choiceCustom: "指定",
  choiceNone: "なし",
  none: "なし",
  /** 発行の確認の表の見出し */
  colSeq: "回",
  colDue: "お支払期限",
  colAmount: "金額（税込）",
  colPaid: "入金",
  colBalance: "残",
  seqLabel: (n: number) => `第${n}回`,
  seqOf: (seq: number, count: number) => `第${seq}回／全${count}回`,
  /** 請求書の画面の「お支払い予定」と紙 */
  schedule: "お支払い予定",
  scheduleHelp: "入金は請求書に当て、期日の古い回から順に当てたとみなして回ごとの残を出します。",
  planOf: (count: number, interval: number) => `分割払い ${count}回・${intervalLabel(interval)}`,
  total: "合計",
  /** 紙のお支払期限の後ろ（第1回） */
  firstSuffix: "（第1回）",
  /** {customer}の画面の「入金予定」 */
  receivables: "入金予定",
  receivablesHelp: "{customer}の請求書の、回ごとの入金予定です（分割払いは回ごと・一括はお支払期限に全額）。入金は期日の古い回から当てたとみなします。今月より前は残のある回だけ出します。",
  noDue: "期限なし",
  monthLabel: (month: string) => (month ? `${Number(month.slice(0, 4))}年${Number(month.slice(5, 7))}月` : "期限なし"),
  /** API の断り */
  noDueDate: "お支払期限が空です。分割払いの予定は、お支払期限から作ります",
} as const;
